import { useState, useEffect } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import type { Empresa } from '@/lib/types'
import { IconCheckCircle, IconXCircle, IconExternalLink } from '@/components/icons/Icons'

export default function GoogleWorkspaceCard() {
  const db = useSupabase()
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [googleEmail, setGoogleEmail] = useState('')
  const [scopes, setScopes] = useState<string[]>([])

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      // El estado de conexión vive en columnas que GET /api/empresa ya NO expone
      // (google_connected/google_tokens se removieron por seguridad). Se lee por un
      // endpoint admin dedicado que reporta el estado SIN devolver los tokens.
      const status = await api.get<{ connected: boolean; email: string | null; scope: string | null }>('/api/google/status')
      setConnected(!!status?.connected)
      setGoogleEmail(status?.email ?? '')
      const scopeStr = status?.scope ?? ''
      setScopes(scopeStr.split(' ').filter(Boolean).map(s => {
        if (s.includes('documents')) return 'Google Docs'
        if (s.includes('calendar')) return 'Google Calendar'
        if (s.includes('contacts')) return 'Google Contacts'
        if (s.includes('userinfo')) return 'Perfil'
        return s.split('/').pop() ?? s
      }))
    } catch { /* non-critical */ }
    finally { setLoading(false) }
  }

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const { data, error } = await supabase.functions.invoke('google-auth-url')
      if (error) throw error
      if (data?.url) {
        window.open(data.url, '_blank')
      } else {
        showToast('No se pudo obtener la URL de autorizacion', 'error')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al conectar', 'error')
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      const empresas = await db.getAll<Empresa>('empresa')
      if (empresas.length > 0) {
        await db.update('empresa', {
          id: empresas[0].id,
          google_tokens: null,
          google_connected: false,
        } as Record<string, unknown>)
        setConnected(false)
        setGoogleEmail('')
        setScopes([])
        showToast('Google Workspace desconectado', 'info')
      }
    } catch {
      showToast('Error al desconectar', 'error')
    } finally {
      setDisconnecting(false)
    }
  }

  if (loading) {
    return (
      <div className="google-workspace-card">
        <div className="google-workspace-card__header">
          <GoogleIcon size={20} />
          <h3>Google Workspace</h3>
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          <div className="auth-spinner" style={{ margin: '0 auto 8px', width: 18, height: 18 }} />
          Cargando...
        </div>
      </div>
    )
  }

  return (
    <div className="google-workspace-card">
      <div className="google-workspace-card__header">
        <GoogleIcon size={20} />
        <h3>Google Workspace</h3>
        <span className={`google-workspace-card__status ${connected ? 'google-workspace-card__status--connected' : ''}`}>
          {connected ? (
            <><IconCheckCircle size={12} /> Conectado</>
          ) : (
            <><IconXCircle size={12} /> Desconectado</>
          )}
        </span>
      </div>

      {connected ? (
        <div className="google-workspace-card__body">
          {googleEmail && (
            <div className="google-workspace-card__info">
              <span className="google-workspace-card__info-label">Cuenta</span>
              <span className="google-workspace-card__info-value">{googleEmail}</span>
            </div>
          )}
          {scopes.length > 0 && (
            <div className="google-workspace-card__info">
              <span className="google-workspace-card__info-label">Permisos</span>
              <div className="google-workspace-card__scopes">
                {scopes.map((s, i) => (
                  <span key={i} className="google-workspace-card__scope-badge">{s}</span>
                ))}
              </div>
            </div>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            style={{ marginTop: 12 }}
          >
            {disconnecting ? 'Desconectando...' : 'Desconectar'}
          </button>
        </div>
      ) : (
        <div className="google-workspace-card__body">
          <p className="google-workspace-card__desc">
            Conecta tu cuenta de Google para crear documentos, sincronizar calendario y acceder a contactos.
          </p>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <div className="auth-spinner" style={{ width: 14, height: 14 }} />
                Conectando...
              </>
            ) : (
              <>
                <IconExternalLink size={14} />
                Conectar Google Workspace
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

/** Google "G" icon — stroke-based SVG */
function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2a9.97 9.97 0 0 1 7.071 2.929" />
      <path d="M22 12h-10" />
      <path d="M22 12a10 10 0 0 1-3.826 7.884" />
    </svg>
  )
}
